import {
  Form,
  Input,
  ModalProps,
  notification,
  type GetProp,
  type InputProps,
} from 'antd'
import { FunctionComponent, useEffect, useState, type ReactNode } from 'react'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import { BasicModal } from '@/components/common/antd/basic-modal'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

interface SingleInputFieldModalFormData {
  value: string
}

type Rules = GetProp<typeof Form.Item, 'rules'>

interface SingleInputFieldModalProps
  extends Pick<ModalProps, 'open' | 'getContainer'> {
  value?: string
  title: string
  description?: ReactNode
  fieldLabel: string
  placeholder?: I18nId
  required?: boolean
  rules?: Rules
  inputProps?: InputProps
  onClose: VoidFunction
  onSave: (value: string) => Promise<void>
}

const SingleInputFieldModal: FunctionComponent<SingleInputFieldModalProps> = ({
  value,
  title,
  description,
  fieldLabel,
  placeholder = 'account:personal_info_field_cs_input_placeholder',
  open,
  onClose,
  onSave,
  required = true,
  getContainer = false,
  rules,
  inputProps,
}) => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm<SingleInputFieldModalFormData>()

  const [loading, setLoading] = useState(false)

  const handleFinish = async (
    formData: SingleInputFieldModalFormData,
  ): Promise<void> => {
    try {
      setLoading(true)

      await onSave(formData.value)

      onClose()
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({ id: 'autoreport:report_update_failed' }),
        placement: 'bottomRight',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) {
      form.resetFields()
    }
  }, [form, open])

  useEffect(() => {
    form.setFieldValue(
      nameof<SingleInputFieldModalFormData>((s) => s.value),
      value,
    )
  }, [form, value])

  return (
    <BasicModal
      centered
      cancelButtonProps={{ style: { display: 'none' } }}
      getContainer={getContainer}
      okButtonProps={{ loading }}
      okText={formatMessage({ id: 'vip:checkout_coupon_submit_btn' })}
      open={open}
      title={title}
      onCancel={(): void => {
        form.resetFields()
        onClose()
      }}
      onOk={form.submit}
    >
      {description && <div>{description}</div>}
      <FormWrapper>
        <Form form={form} onFinish={toVoidFunction(handleFinish)}>
          <Form.Item
            colon={false}
            initialValue={value}
            label={fieldLabel}
            labelCol={{ span: 24 }}
            name={nameof<SingleInputFieldModalFormData>((r) => r.value)}
            rules={[
              {
                required,
                message: formatMessage(
                  { id: 'error:please_fulfill' },
                  { inputField: fieldLabel },
                ),
              },
              ...(rules ?? []),
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: placeholder,
              })}
              size='large'
              {...inputProps}
            />
          </Form.Item>
        </Form>
      </FormWrapper>
    </BasicModal>
  )
}

const FormWrapper = styled.div`
  .ant-form-item {
    margin: 0;
  }

  .ant-form-item-label {
    padding: 9px 0;
  }
`

export default SingleInputFieldModal
